/**
 * PostHog event names — single source of truth for product analytics.
 *
 * Pair each constant with `useAnalytics().track(AnalyticsEvent.X, props?)`.
 * Comments describe intent, typical props, and where the event fires.
 */

export const AnalyticsEvent = {
  // --- Acquisition (install / cold start) ---
  /**
   * First time this install opens the app (once per device, persisted in Preferences).
   * Props: platform, app_version, utm_source, utm_medium, utm_campaign, utm_content, utm_term, referrer_url, campaign_id.
   */
  APP_FIRST_OPENED: 'App First Opened',
  /** App was opened via deep link / URL scheme. Fires once per install. Props: url, utm_*, referrer_url, campaign_id. */
  APP_OPENED_VIA_DEEP_LINK: 'App Opened Via Deep Link',

  // --- Subscription & pricing (in-app) ---
  /** Limited-plan upgrade drawer became visible. No props. */
  PLAN_SELECT_DRAWER_OPENED: 'Plan Select Drawer Opened',
  /** User tapped Subscribe in the plan drawer; Stripe session creation begins. Props: planType, previousStatus (Stripe status or "free"). */
  SUBSCRIPTION_STARTED: 'Subscription Started',
  /** Checkout opened in a new window/tab. Props: planType. */
  CHECKOUT_REDIRECTED: 'Checkout Redirected',
  /** Popup blocked or new window unavailable; user sees link instead. Props: planType. */
  CHECKOUT_LINK_SHOWN: 'Checkout Link Shown',
  /** Plan drawer checkout failed before redirect. Props: planType, error. */
  SUBSCRIPTION_ERROR: 'Subscription Error',

  /** Standalone `/pricing` route loaded (web; fires before native early-exit redirect). Props: hasExpiredSubscription, etc. */
  PRICING_PAGE_VISIT: 'Pricing Page Visit',
  /** Standalone `/pricing` plan CTA only (not trial FAQ — that uses TRIAL_STARTED). Props: planType. */
  CHECKOUT_INITIATED: 'Checkout Initiated',
  /** `/pricing` checkout failed. Props: planType, error. */
  CHECKOUT_ERROR: 'Checkout Error',
  /** Logged-in user with active/trialing sub was sent away from `/pricing`. Props: stripeSubscriptionStatus. */
  ACTIVE_USER_REDIRECTED: 'Active User Redirected',

  // --- Trial onboarding ---
  /** User started Stripe checkout for trial from trial FAQ. Props: planType. */
  TRIAL_STARTED: 'Trial Started',
  /** Trial checkout failed. Props: planType, error. */
  TRIAL_ERROR: 'Trial Error',
  /** User returned from Stripe to trial FAQ with checkoutComplete query. No props. */
  TRIAL_STRIPE_RETURNED: 'Trial Stripe Returned',

  // --- Onboarding (shared step funnel) ---
  /** Screen impression during onboarding. Props: step (string id, e.g. trial-faq, add-accounts). */
  ONBOARDING_STEP_VIEWED: 'Onboarding Step Viewed',
  /** Marketing email opt-in on setup complete. Props: optedIn. */
  MARKETING_OPT_IN_SELECTED: 'Marketing Opt-In Selected',
  /** Continue tapped from add-income step. Props vary by implementation. */
  CONTINUE_FLOW_CLICKED: 'Continue Flow Clicked',
  /** User finished add-expenses step. */
  ONBOARDING_EXPENSES_COMPLETED: 'Onboarding Expenses Completed',
  /** User finished linking accounts during onboarding. Props: accountCount. */
  FINISHED_ADDING_ACCOUNTS: 'Finished Adding Accounts',
  /** Plaid or add-account flow started from onboarding. Props: isUpdate. */
  ADD_ACCOUNT_INITIATED: 'Add Account Initiated',
  /** Plaid link success path. Props include institution metadata. */
  ACCOUNT_ADDED_SUCCESSFULLY: 'Account Added Successfully',
  /** Plaid Link UI finished loading. */
  PLAID_LINK_LOADED: 'Plaid Link Loaded',
  /** User closed Plaid Link. Props: metadata. */
  PLAID_LINK_EXITED: 'Plaid Link Exited',
  /** Forwarded Plaid onEvent callback for debugging. Props: eventName, metadata. */
  PLAID_LINK_EVENT: 'Plaid Link Event',
  /** Add account failed in onboarding. Props: error. */
  ADD_ACCOUNT_ERROR: 'Add Account Error',

  // --- Signup & email verification ---
  FIRST_NAME_ENTERED: 'First Name Entered',
  LAST_NAME_ENTERED: 'Last Name Entered',
  EMAIL_ENTERED: 'Email Entered',
  REFERRAL_SOURCE_ENTERED: 'Referral Source Entered',
  SIGNUP_STEP_1_COMPLETED: 'Signup Step 1 Completed',
  SIGNUP_STEP_1_FAILED: 'Signup Step 1 Failed',
  PASSWORD_ENTERED: 'Password Entered',
  SIGNUP_STEP_2_COMPLETED: 'Signup Step 2 Completed',
  SIGNUP_STEP_2_FAILED: 'Signup Step 2 Failed',
  SIGNUP_COMPLETED: 'Signup Completed',
  VERIFICATION_CODE_ENTERED: 'Verification Code Entered',
  VERIFICATION_CODE_RESEND_FAILED: 'Verification Code Resend Failed',
  VERIFICATION_CODE_RESENT: 'Verification Code Resent',
  EMAIL_VERIFICATION_FAILED: 'Email Verification Failed',
  EMAIL_VERIFICATION_SUCCEEDED: 'Email Verification Succeeded',

  // --- Password flows (legacy PostHog casing preserved) ---
  /** Forgot-password flow completed successfully. */
  PASSWORD_RESET: 'Password reset',
  PASSWORD_RESET_ERROR: 'Password reset error',
  PASSWORD_CHANGED: 'Password changed',
  /** Includes mistaken use on forgot-password email submit; props: error. */
  PASSWORD_CHANGE_ERROR: 'Password change error',

  // --- Misc auth / legal ---
  TERMS_OF_SERVICE_VIEWED: 'Terms Of Service Viewed',

  // --- Returning users (login) ---
  /** Email/password or biometric session established. Props: method ('password' | 'biometric'). */
  LOGIN_SUCCEEDED: 'Login Succeeded',
  /** Sign-in failed. Props: method, errorCode (Cognito or short code; not raw messages). */
  LOGIN_FAILED: 'Login Failed',

  /** Stripe Customer Portal opened from profile (Plan & Subscription). No props. */
  STRIPE_PORTAL_OPENED: 'Stripe Portal Opened',

  /** Main Balance tab loaded. No props. */
  BALANCE_PAGE_VIEWED: 'Balance Page Viewed',

  /** Legacy checkout return route mounted (polling may be disabled). No props. */
  CHECKOUT_SUCCESS_PAGE_VIEWED: 'Checkout Success Page Viewed',

  /** Subscription / signup banner shown above accounts content. Props: variant ('expired' | 'finish_signup'). */
  SUBSCRIPTION_BANNER_VIEWED: 'Subscription Banner Viewed',
  /** User opened plan select from banner CTA (same drawer as limited-plan upgrade). Props: variant (same as banner). */
  SUBSCRIPTION_BANNER_PLAN_SELECT_CLICKED:
    'Subscription Banner Plan Select Clicked',

  // --- Accounts & balance ---
  COMPARISON_LINK_CLICKED: 'Comparison Link Clicked',
  ACCOUNTS_PAGE_VIEWED: 'Accounts Page Viewed',
  ACCOUNT_DETAIL_NAVIGATION: 'Account Detail Navigation',
  ACCOUNT_SETTINGS_PAGE_VIEWED: 'Account Settings Page Viewed',
  ACCOUNT_TRANSACTIONS_VISIBILITY_TOGGLED:
    'Account Transactions Visibility Toggled',
  ACCOUNT_DETAIL_PAGE_VIEWED: 'Account Detail Page Viewed',
  ACCOUNT_BALANCE_INCLUSION_TOGGLED: 'Account Balance Inclusion Toggled',
  DELETE_ACCOUNT_CONFIRMATION_FAILED: 'Delete Account Confirmation Failed',
  ACCOUNT_DELETED: 'Account Deleted',

  // --- Plaid (composable / native) ---
  PLAID_HOSTED_LINK_OPENING: 'Plaid Hosted Link Opening',
  ACCOUNTS_REFRESHED_AFTER_PLAID_SESSION_DELAYED:
    'Accounts Refreshed After Plaid Session (Delayed)',
  ACCOUNT_REFRESH_ERROR_AFTER_PLAID_SESSION:
    'Account Refresh Error After Plaid Session',
  WEB_AUTHENTICATION_SESSION_ERROR: 'WebAuthenticationSession Error',
  BROWSER_FINISHED_REFRESHING_ACCOUNTS:
    'Browser Finished - Refreshing Accounts',
  ACCOUNTS_REFRESHED_AFTER_PLAID_BROWSER_CLOSE:
    'Accounts Refreshed After Plaid Browser Close',
  ACCOUNT_REFRESH_ERROR_AFTER_BROWSER_CLOSE:
    'Account Refresh Error After Browser Close',
  LINK_TOKEN_FETCH_ERROR: 'Link Token Fetch Error',

  // --- Bug reporting ---
  BUG_REPORT_PROMPT_OPENED: 'Bug Report Prompt Opened',
  BUG_REPORT_STARTED: 'Bug Report Started',
  BUG_REPORT_SUBMITTED: 'Bug Report Submitted',
  BUG_REPORT_SUBMIT_FAILED: 'Bug Report Submit Failed',
  BUG_REPORT_CANCELLED: 'Bug Report Cancelled',
  SHAKE_TO_REPORT_TOGGLED: 'Shake To Report Toggled',

  // --- Categories & expenses ---
  /** User added a template expense line from recommendations. Props: categoryName. */
  SUGGESTED_EXPENSE_CATEGORY_ADDED: 'Suggested Expense Category Added',
  /** User-added non-template expense during onboarding bulk add. Props: categoryName. */
  CUSTOM_EXPENSE_CATEGORY_ADDED: 'Custom Expense Category Added',
  EXPENSES_ENTERED: 'Expenses Entered',
  CATEGORY_EDITED: 'Category Edited',
  CATEGORY_ADDED: 'Category Added',
  CATEGORY_DEACTIVATED: 'Category Deactivated',
} as const

export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent]
